/* eslint-disable @typescript-eslint/naming-convention */
import { createCanvas, loadImageAsync } from '@/utils/file-kit';
import type { IBackgroundLayerProps, IImageLayerProps, ILayerProps } from '@/version-types';
import type { ServiceManager } from '../service-manager';
import { EdgelessBoundingBox } from './bounding-box';
import type { EdgelessObject } from './edgeless-object';
import { getLayerAnimationFrame } from './layer-animation';

type LayerAnimation = NonNullable<ILayerProps['__animation']>;

interface IAnimationDrawEntry {
    layerId: string;
    animations: LayerAnimation[];
}

/** 非无限画布 frame 投影，对应 box-shadow: 0 4px 20px var(--stroke-system-border-overlay) */
const FRAME_SHADOW_OFFSET_Y = 4;
const FRAME_SHADOW_BLUR = 20;

/** 导航预览缓存的最大实际像素宽度，避免高分屏生成过大的缩略图 */
const PREVIEW_MAX_WIDTH = 400;

let cachedFrameShadowColor: string | null = null;
let cachedFrameShadowFill: string | null = null;

const getFrameShadowColor = () => {
    if (cachedFrameShadowColor) return cachedFrameShadowColor;
    cachedFrameShadowColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--stroke-system-border-overlay')
        .trim();
    return cachedFrameShadowColor;
};

const getFrameShadowFill = () => {
    if (cachedFrameShadowFill) return cachedFrameShadowFill;
    cachedFrameShadowFill = getComputedStyle(document.documentElement)
        .getPropertyValue('--background-system-frame-floatpanel')
        .trim();
    return cachedFrameShadowFill;
};

export class EdgelessFrame extends EdgelessBoundingBox {
    readonly frameId: number;
    readonly service: ServiceManager;

    readonly canvas: HTMLCanvasElement;
    /** 导航专用缓存，避免封面绘制覆盖主画布的动画帧 */
    readonly previewCanvas: HTMLCanvasElement;
    private __ctx: CanvasRenderingContext2D;
    private __previewTimer: NodeJS.Timeout | null = null;
    private __maskURL = '';
    private __maskImage: HTMLImageElement | null = null;
    private __maskLoadingPromise: Promise<HTMLImageElement | null> | null = null;
    private __maskEnabled = true;
    private __clipEnabled = true;

    constructor(frameId: number, service: ServiceManager) {
        super();
        this.frameId = frameId;
        this.service = service;
        const { canvas, ctx } = createCanvas(10, 10);
        this.canvas = canvas;
        this.__ctx = ctx;
        this.previewCanvas = createCanvas(10, 10).canvas;
        this.update();
    }

    /** 设置是否绘制 frame mask */
    setMaskEnabled(enabled: boolean) {
        if (this.__maskEnabled === enabled) return;
        this.__maskEnabled = enabled;
        this.service.edgelessCanvas.addDirtyFrameId(this.frameId);
        this.service.edgelessCanvas.requestRenderAll();
    }

    /** 获取 frame mask 绘制状态 */
    isMaskEnabled() {
        return this.__maskEnabled;
    }

    /** 设置是否对 frame 内容进行裁剪 */
    setClipEnabled(enabled: boolean) {
        if (this.__clipEnabled === enabled) return;
        this.__clipEnabled = enabled;
        this.service.edgelessCanvas.addDirtyFrameId(this.frameId);
        this.service.edgelessCanvas.requestRenderAll();
    }

    /** 获取 frame 内容裁剪状态 */
    isClipEnabled() {
        return this.__clipEnabled;
    }

    /** 渲染对象并裁剪 */
    renderObjectsByClip(ctx: CanvasRenderingContext2D, objects: EdgelessObject[], clipEnabled = true) {
        const { frame, edgelessCanvas } = this.service;
        const clipArea = frame.getBaseInfo(this.frameId);
        ctx.save();
        const { zoom, translateX, translateY } = edgelessCanvas.renderZoomAndTranslate;
        if (clipEnabled) {
            ctx.beginPath();
            ctx.rect(
                clipArea.left * zoom + translateX,
                clipArea.top * zoom + translateY,
                clipArea.width * zoom,
                clipArea.height * zoom,
            );
            ctx.clip();
        }
        for (const object of objects) {
            edgelessCanvas.renderObject(ctx, object);
        }
        ctx.restore();
    }

    /** 渲染预览 */
    renderPreview() {
        if (this.service.edgelessCanvas.isEdgelessMode) return;
        // 动态素材持续刷新时也要生成预览，避免尾沿防抖让缩略图一直空白。
        if (this.__previewTimer) return;
        this.__previewTimer = setTimeout(() => {
            this.__previewTimer = null;
            const { flatLayer, frame, edgelessCanvas } = this.service;
            const size = frame.getSize(this.frameId);
            if (size.width <= 0 || size.height <= 0) return;
            const canvas = this.previewCanvas;
            // 脏标记会覆盖所有页；未触发的页只通知导航复用缓存，避免预览提前加载蒙版等资源。
            if (edgelessCanvas.hasDeferredFrameResources(this.frameId)) {
                frame.__emitPreviewComplete({ frameId: this.frameId, canvas });
                return;
            }
            canvas.width = Math.max(
                1,
                Math.min(PREVIEW_MAX_WIDTH, Math.round(size.width * edgelessCanvas.retinaScaling)),
            );
            const previewScale = canvas.width / size.width;
            canvas.height = Math.max(1, Math.round(size.height * previewScale));
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            /** 渲染普通图层 */
            flatLayer.traverseIds(true, frame.getLayerIds(this.frameId), (layerId: string) => {
                this.__drawRelativeLayerTo(ctx, layerId, undefined, true, previewScale);
            });
            this.__loadFrameMaskImage(this.__getFrameMaskURL());
            this.__applyFrameMaskOnContext(ctx, 0, 0, canvas.width, canvas.height);
            this.__drawFrameWatermark(ctx, previewScale);

            frame.__emitPreviewComplete({ frameId: this.frameId, canvas });
        }, 500);
    }

    /**
     * 在目标 Canvas 中播放给定图层快照的入场动画。
     *
     * 图层内容仍由当前 Frame 的 EdgelessObject 绘制，传入数据只控制本次播放的
     * 图层树、隐藏状态与动画参数，避免修改编辑器中的正式图层数据。
     * @param targetCanvas - 持续绘制演示内容的目标画布
     * @param layers - 本次演示的图层与入场动画配置
     * @returns 停止本次播放并释放 requestAnimationFrame 的函数
     */
    playLayerAnimation(targetCanvas: HTMLCanvasElement, layers: ILayerProps[]) {
        const targetContext = targetCanvas.getContext('2d');
        if (!targetContext) return () => undefined;

        const entries: IAnimationDrawEntry[] = [];
        let requiresContinuousRender = false;
        const collectEntries = (layer: ILayerProps, inheritedAnimations: LayerAnimation[]) => {
            if (layer.hidden) return;
            if (this.service.edgelessCanvas.getByLayerId(layer.id)?.requiresContinuousRender) {
                requiresContinuousRender = true;
            }

            const animation = layer.__animation;
            const animations =
                animation?.enabled && animation.name ? [...inheritedAnimations, animation] : inheritedAnimations;
            layer.children?.forEach((child) => collectEntries(child, animations));
            entries.push({ layerId: layer.id, animations });
        };
        layers.forEach((layer) => collectEntries(layer, []));

        const { edgelessCanvas, frame } = this.service;
        edgelessCanvas.requestFrameResources(this.frameId);
        const retinaScaling = edgelessCanvas.retinaScaling;
        const size = frame.getSize(this.frameId);
        targetCanvas.width = size.width * retinaScaling;
        targetCanvas.height = size.height * retinaScaling;

        let animationFrame = 0;
        let disposed = false;
        const startTime = performance.now();
        const render = (currentTime: number) => {
            if (disposed) return;
            animationFrame = 0;

            const elapsedSeconds = (currentTime - startTime) / 1000;
            let complete = true;
            targetContext.clearRect(0, 0, targetCanvas.width, targetCanvas.height);

            entries.forEach(({ layerId, animations }) => {
                let opacity = 1;
                let offsetX = 0;
                let offsetY = 0;
                animations.forEach((animation) => {
                    const state = getLayerAnimationFrame(animation, elapsedSeconds);
                    opacity *= state.opacity;
                    offsetX += state.offsetX;
                    offsetY += state.offsetY;
                    complete = complete && state.complete;
                });
                if (opacity <= 0) return;
                this.__drawRelativeLayerTo(targetContext, layerId, { opacity, offsetX, offsetY });
            });

            this.__applyFrameMaskOnContext(targetContext, 0, 0, targetCanvas.width, targetCanvas.height);
            this.__drawFrameWatermark(targetContext);

            if (!complete || requiresContinuousRender) {
                animationFrame = requestAnimationFrame(render);
            }
        };

        // 首次资源可能晚于入场动画结束；就绪事件补绘静态页，不启动常驻轮询。
        const previewHandler = frame.onPreviewComplete(({ frameId }) => {
            if (frameId === this.frameId && !animationFrame) render(performance.now());
        });
        render(startTime);

        return () => {
            disposed = true;
            this.service.events.off(previewHandler);
            if (animationFrame) cancelAnimationFrame(animationFrame);
        };
    }

    renderInSingle(ctx: CanvasRenderingContext2D) {
        const { flatLayer, frame, edgelessCanvas } = this.service;
        const objects: EdgelessObject[] = [];
        /** 渲染普通图层 */
        flatLayer.traverseIds(true, frame.getLayerIds(this.frameId), (layerId: string) => {
            const object = this.__getObject(layerId, { ignoreFrameVisible: !this.__clipEnabled });
            if (!object) return;
            objects.push(object);
        });

        const clipArea = frame.getBaseInfo(this.frameId);
        const { zoom, translateX, translateY } = edgelessCanvas.renderZoomAndTranslate;
        this.__renderFrameShadow(
            ctx,
            clipArea.left * zoom + translateX,
            clipArea.top * zoom + translateY,
            clipArea.width * zoom,
            clipArea.height * zoom,
        );
        this.renderObjectsByClip(ctx, objects, this.__clipEnabled);
        this.__applyFrameMaskOnContext(
            ctx,
            clipArea.left * zoom + translateX,
            clipArea.top * zoom + translateY,
            clipArea.width * zoom,
            clipArea.height * zoom,
        );
        // 全局 SVIP 水印
        this.service.svipWatermark.drawFrameWatermark(
            this.frameId,
            ctx,
            clipArea.left * zoom + translateX,
            clipArea.top * zoom + translateY,
            clipArea.width * zoom,
            clipArea.height * zoom,
        );
        frame.__emitRenderComplete({ frameId: this.frameId, canvas: this.canvas });
    }

    renderInVariety(ctx: CanvasRenderingContext2D) {
        const { frame, flatLayer, edgelessCanvas } = this.service;

        if (!(edgelessCanvas.dirtyFrameIds.has(this.frameId) || this.checkInViewport().intersects)) return;
        const { zoom, translateX, translateY } = edgelessCanvas.renderZoomAndTranslate;

        ctx.save();
        ctx.translate(this.left * zoom + translateX, this.top * zoom + translateY);
        ctx.scale(zoom, zoom);
        // console.log('renderInVariety =>', this.frameId, { top: this.top, translateY });

        const renderFrameIds = frame.getRenderFrameIds();
        const frameIndex = renderFrameIds.indexOf(this.frameId);
        const frameCount = renderFrameIds.length;
        this.__renderFrameShadow(ctx, 0, 0, this.width, this.height, zoom, {
            suppressTop: frameIndex > 0,
            suppressBottom: frameIndex !== -1 && frameIndex < frameCount - 1,
        });

        if (!edgelessCanvas.dirtyFrameIds.has(this.frameId)) {
            // console.log('render frame not dirty <==>', this.frameId);
            ctx.drawImage(this.canvas, 0, 0, this.width, this.height);
            ctx.restore();
            return;
        }
        // console.log('render frame dirty =>', this.frameId);
        this.__resetCanvas();

        /** 渲染图层 */
        flatLayer.traverseIds(true, frame.getLayerIds(this.frameId), (layerId: string) => {
            this.__drawRelativeLayer(layerId);
            // console.log('renderInVariety', layerId);
        });

        this.__applyFrameMaskOnCanvas();
        // 全局 SVIP 水印（绘制在 frame 离屏 canvas 上）
        this.__drawFrameWatermarkOnCanvas();

        // console.log('renderFrame', this.id, this.canvas);
        ctx.drawImage(this.canvas, 0, 0, this.width, this.height);
        ctx.restore();

        frame.__emitRenderComplete({ frameId: this.frameId, canvas: this.canvas });
    }

    renderInEdgeless(ctx: CanvasRenderingContext2D) {
        const { frame, flatLayer, edgelessCanvas } = this.service;

        if (!(edgelessCanvas.dirtyFrameIds.has(this.frameId) || this.checkInViewport().intersects)) return;
        const { zoom, translateX, translateY } = edgelessCanvas.renderZoomAndTranslate;
        ctx.save();
        try {
            ctx.translate(this.left * zoom + translateX, this.top * zoom + translateY);
            ctx.scale(zoom, zoom);

            if (!edgelessCanvas.dirtyFrameIds.has(this.frameId)) {
                // console.log('render frame not dirty =>', this.frameId);
                ctx.drawImage(this.canvas, 0, 0, this.width, this.height);
                return;
            }
            // console.log('render frame dirty =>', this.frameId);
            this.__resetCanvas();

            const stickyOutIds = frame.getStickyOutFlatIds(this.frameId);
            /** 渲染普通图层 */
            flatLayer.traverseIds(true, frame.getLayerIds(this.frameId), (layerId: string) => {
                if (stickyOutIds.has(layerId)) return;
                this.__drawRelativeLayer(layerId);
            });

            /** 渲染粘性移入图层 */
            frame.traverseStickyIn(this.frameId, (layerId: string) => {
                // console.log('stickyIn', layerId);
                this.__drawRelativeLayer(layerId);
            });

            this.__applyFrameMaskOnCanvas();
            // 全局 SVIP 水印（绘制在 frame 离屏 canvas 上）
            this.__drawFrameWatermarkOnCanvas();

            // console.log('renderFrame', this.id, this.canvas);
            ctx.drawImage(this.canvas, 0, 0, this.width, this.height);
        } finally {
            ctx.restore();
        }

        frame.__emitRenderComplete({ frameId: this.frameId, canvas: this.canvas });
    }

    private __resetCanvas() {
        const { edgelessCanvas, frame } = this.service;
        const size = frame.getSize(this.frameId);
        const retinaScaling = edgelessCanvas.retinaScaling;
        this.canvas.width = size.width * retinaScaling;
        this.canvas.height = size.height * retinaScaling;
        this.__ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    /** 绘制 frame 投影（非无限画布） */
    private __renderFrameShadow(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        localZoom = 1,
        edgeOptions?: { suppressTop?: boolean; suppressBottom?: boolean },
    ) {
        if (!this.__clipEnabled) return;

        const blur = Math.min(FRAME_SHADOW_BLUR * localZoom, FRAME_SHADOW_BLUR);
        const offsetY = Math.min(FRAME_SHADOW_OFFSET_Y * localZoom, FRAME_SHADOW_OFFSET_Y);
        const pad = blur + offsetY;

        ctx.save();

        if (edgeOptions?.suppressTop || edgeOptions?.suppressBottom) {
            const clipLeft = x - pad;
            const clipWidth = width + pad * 2;
            const clipTop = edgeOptions.suppressTop ? y : y - pad;
            const clipBottom = edgeOptions.suppressBottom ? y + height : y + height + pad;
            ctx.beginPath();
            ctx.rect(clipLeft, clipTop, clipWidth, clipBottom - clipTop);
            ctx.clip();
        }

        const backgroundLayer = this.service.frame.getBackgroundLayer(this.frameId);
        const dynamicInfo = (backgroundLayer?.data as IBackgroundLayerProps | undefined)?.dynamicInfo;
        if (dynamicInfo && !dynamicInfo.hidden) {
            // 动态背景由 Canvas 下方的 DOM 层绘制。这里排除 Frame 内部，只保留外部投影，
            // 避免用于生成阴影的白色填充挡住动态背景。
            ctx.beginPath();
            ctx.rect(x - pad, y - pad, width + pad * 2, height + pad * 2);
            ctx.rect(x, y, width, height);
            ctx.clip('evenodd');
        }

        if (getFrameShadowColor() && getFrameShadowFill()) {
            ctx.shadowColor = getFrameShadowColor();
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = offsetY;
            ctx.shadowBlur = blur;
            ctx.fillStyle = getFrameShadowFill();
            ctx.fillRect(x, y, width, height);
        }

        ctx.restore();
    }

    /** 绘制相对位置图层 */
    private __drawRelativeLayer(layerId: string) {
        this.__drawRelativeLayerTo(this.__ctx, layerId);
    }

    /** 将相对位置图层绘制到指定上下文 */
    private __drawRelativeLayerTo(
        context: CanvasRenderingContext2D,
        layerId: string,
        animation: { opacity: number; offsetX: number; offsetY: number } = { opacity: 1, offsetX: 0, offsetY: 0 },
        isPreview = false,
        renderScale = this.service.edgelessCanvas.retinaScaling,
    ) {
        const object = this.__getObject(layerId);
        if (!object) return;

        const position = object.getPositionInFrame(this.frameId);
        if (!position) return;
        // if (layerId.indexOf('0087303b-15f0-46bc-bbd0-1f0776cc002b') !== -1) {
        //     console.log('drawRelativeLayer', layerId, position);
        // }
        context.save();
        try {
            context.translate(
                (position.left + animation.offsetX) * renderScale,
                (position.top + animation.offsetY) * renderScale,
            );
            context.rotate((Math.PI / 180) * object.angle);
            context.scale(renderScale, renderScale);
            const opacity = object.opacity * animation.opacity;
            if (opacity !== 1) {
                context.globalAlpha = opacity;
            }
            if (isPreview) object.renderPreview(context);
            else object.render(context);
        } catch (error) {
            console.error('Frame layer render failed', { frameId: this.frameId, layerId }, error);
        } finally {
            context.restore();
        }
    }

    /** 在 frame 离屏 canvas 上绘制全局 SVIP 水印 */
    private __drawFrameWatermark(
        context: CanvasRenderingContext2D,
        renderScale = this.service.edgelessCanvas.retinaScaling,
    ) {
        const { frame, svipWatermark } = this.service;
        const size = frame.getSize(this.frameId);
        context.save();
        context.scale(renderScale, renderScale);
        svipWatermark.drawFrameWatermark(this.frameId, context, 0, 0, size.width, size.height);
        context.restore();
    }

    /** 在 frame 离屏 canvas 上绘制全局 SVIP 水印 */
    private __drawFrameWatermarkOnCanvas() {
        this.__drawFrameWatermark(this.__ctx);
    }

    private __getFrameMaskURL() {
        const mainLayer = this.service.frame.getMainLayer(this.frameId);
        return ((mainLayer?.data as IImageLayerProps | undefined)?.maskURL || '').trim();
    }

    private __loadFrameMaskImage(maskURL: string) {
        if (this.__maskURL !== maskURL) {
            this.__maskURL = maskURL;
            this.__maskImage = null;
            this.__maskLoadingPromise = null;
        }

        if (!maskURL || this.__maskImage || this.__maskLoadingPromise) return;

        this.__maskLoadingPromise = loadImageAsync(maskURL)
            .then((image) => {
                if (this.__maskURL !== maskURL) return null;
                this.__maskImage = image;
                this.service.edgelessCanvas.addDirtyFrameId(this.frameId);
                this.service.edgelessCanvas.requestRenderAll();
                return image;
            })
            .catch(() => null)
            .finally(() => {
                if (this.__maskURL === maskURL) {
                    this.__maskLoadingPromise = null;
                }
            });
    }

    private __applyFrameMaskOnCanvas() {
        if (!this.__maskEnabled) return;

        const maskURL = this.__getFrameMaskURL();
        this.__loadFrameMaskImage(maskURL);
        if (!maskURL || !this.__maskImage) return;

        const { edgelessCanvas, frame } = this.service;
        const retinaScaling = edgelessCanvas.retinaScaling;
        const size = frame.getSize(this.frameId);

        this.__ctx.save();
        this.__ctx.globalCompositeOperation = 'destination-in';
        this.__ctx.drawImage(this.__maskImage, 0, 0, size.width * retinaScaling, size.height * retinaScaling);
        this.__ctx.restore();
    }

    private __applyFrameMaskOnContext(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
    ) {
        if (!this.__maskEnabled) return;

        const maskURL = this.__getFrameMaskURL();
        this.__loadFrameMaskImage(maskURL);
        if (!maskURL || !this.__maskImage) return;

        ctx.save();
        ctx.beginPath();
        ctx.rect(x, y, width, height);
        ctx.clip();
        ctx.globalCompositeOperation = 'destination-in';
        ctx.drawImage(this.__maskImage, x, y, width, height);
        ctx.restore();
    }

    private __getObject(layerId: string, options?: { ignoreFrameVisible?: boolean }) {
        const { frame, edgelessCanvas } = this.service;
        const object = edgelessCanvas.getByLayerId(layerId);
        if (
            object &&
            object.visible &&
            !object.renderHidden &&
            !object.shouldSkipRender() &&
            (options?.ignoreFrameVisible || frame.checkIsVisible(layerId, this.frameId))
        ) {
            return object;
        }
        return null;
    }

    /** 检查对象是否在视口内 */
    checkInViewport(blindAreas?: { top?: number; right?: number; bottom?: number; left?: number }) {
        const { edgelessCanvas } = this.service;
        const translateX = edgelessCanvas.translateX;
        const translateY = edgelessCanvas.translateY;

        return this.getIntersectsInfo(
            edgelessCanvas.zoom,
            edgelessCanvas.size,
            {
                x: translateX,
                y: translateY,
            },
            blindAreas,
        );
    }

    update() {
        const info = this.service.frame.getBaseInfo(this.frameId);
        this.left = info.left;
        this.top = info.top;
        this.width = info.width;
        this.height = info.height;
        this.setCoords();
    }
}
